<script setup lang="ts" generic="TValue extends AcceptableInputValue">
import type { AcceptableInputValue } from 'reka-ui'
import { computed } from 'vue'

import type { AObjectSelectItem } from '../generics'
import AMultiObjectSelect, { type AMultiObjectSelectProperties } from './AMultiObjectSelect.vue'
import AMultiStringSelect, { type AMultiStringSelectProperties } from './AMultiStringSelect.vue'

export interface AMultiSelectProperties {
  multiple?: true
  arrayType?: 'string' | 'object'
  items?: unknown[]
}

export type AMultiSelectType = AMultiSelectProperties['arrayType']

const properties = withDefaults(defineProps<AMultiSelectProperties>(), {
  placeholder: undefined,
  arrayType: undefined,
  disabled: false,
  items: () => [],
})

const modelValue = defineModel<TValue[]>('modelValue', { required: false, default: [] })
const search = defineModel<string>('search', { required: false, default: '' })

// This may be to excessive, good for discussion later works for now.
const selectType = computed((): AMultiSelectType => {
  if (properties.arrayType) return properties.arrayType
  const items = properties.items

  // If the array is empty, we can't determine the type so we fall back to 'string'
  if (items?.length === 0) return 'string'
  const itemsArrayType: AMultiSelectType = items?.length > 0 && typeof items[0] === 'object' ? 'object' : 'string'
  return itemsArrayType
})

const stringItems = computed<string[]>(() => properties.items as string[])
const objectItems = computed<AObjectSelectItem[]>(() => properties.items as AObjectSelectItem[])

const objectProperties = computed<AMultiObjectSelectProperties>(() => properties as AMultiObjectSelectProperties)
const stringProperties = computed<AMultiStringSelectProperties>(() => properties as AMultiStringSelectProperties)
</script>

<template>
  <AMultiStringSelect
    v-if="selectType === 'string'"
    v-bind="{ ...$attrs, ...stringProperties }"
    v-model="modelValue"
    v-model:search="search"
    :items="stringItems"
  >
    <template #item="forwardedProps"><slot name="item" v-bind="forwardedProps" /></template>
    <template #empty><slot name="empty" /></template>
  </AMultiStringSelect>
  <AMultiObjectSelect
    v-if="selectType === 'object'"
    v-bind="{ ...$attrs, ...objectProperties }"
    v-model="modelValue"
    v-model:search="search"
    :items="objectItems"
  >
    <template #item="forwardedProps"><slot name="item" v-bind="forwardedProps" /></template>
    <template #empty><slot name="empty" /></template>
  </AMultiObjectSelect>
</template>
